/**
 * Atlas «next unlock» — для каждой категории вычисляет что ближайшее
 * разблокируется и какое условие.
 *
 * JB 2026-05-26: «в атласе человек знает сколько чего ему еще нужно
 * сделать до новой разблокировки каких то эффектов разного типа».
 *
 * Возвращает строку-подсказку для каждой категории или null если
 * всё открыто. UI рендерит её в шапке категории под progress-bar.
 */
import type { GameState, LessonCondition } from '@sai-clicker/game-core';
import { ALL_PHRASES, LESSONS_ALPHA, UPGRADES_ALPHA, WHISPERS_ALPHA } from '@sai-clicker/content';
import type { VisualArtifact } from '../visual-artifacts/types.js';
import { VISUAL_ARTIFACTS_CATALOG } from '../visual-artifacts/catalog.js';
import type { AtlasCategoryKey } from './atlasRewards.js';

export interface NextUnlockHint {
  /** Имя категории для логирования. */
  readonly category: AtlasCategoryKey;
  /** Краткий one-liner: «следующая фраза откроется при тапе в эпохе 2». */
  readonly hint: string;
}

/**
 * Вычислить hint для каждой категории. Возвращает массив 5 элементов
 * (по одному per category) — null значения отфильтрованы (всё закрыто).
 */
export function computeAllNextUnlocks(
  state: GameState,
  unlockedArtifacts: ReadonlySet<string>,
): readonly NextUnlockHint[] {
  const out: NextUnlockHint[] = [];

  const phrasesHint = nextPhraseHint(state);
  if (phrasesHint !== null) out.push({ category: 'phrases', hint: phrasesHint });

  const lessonsHint = nextLessonHint(state);
  if (lessonsHint !== null) out.push({ category: 'lessons', hint: lessonsHint });

  const artifactsHint = nextArtifactHint(state, unlockedArtifacts);
  if (artifactsHint !== null) out.push({ category: 'artifacts', hint: artifactsHint });

  const upgradesHint = nextUpgradeHint(state);
  if (upgradesHint !== null) out.push({ category: 'upgrades', hint: upgradesHint });

  const whispersHint = nextWhisperHint(state);
  if (whispersHint !== null) out.push({ category: 'whispers', hint: whispersHint });

  return out;
}

/** Lookup за O(1) — возвращает hint для конкретной категории или null. */
export function nextUnlockFor(
  category: AtlasCategoryKey,
  state: GameState,
  unlockedArtifacts: ReadonlySet<string>,
): string | null {
  switch (category) {
    case 'phrases':
      return nextPhraseHint(state);
    case 'lessons':
      return nextLessonHint(state);
    case 'artifacts':
      return nextArtifactHint(state, unlockedArtifacts);
    case 'upgrades':
      return nextUpgradeHint(state);
    case 'whispers':
      return nextWhisperHint(state);
  }
}

// ---------------------------------------------------------------------------
// Per-category logic
// ---------------------------------------------------------------------------

function nextPhraseHint(state: GameState): string | null {
  // Phrases collected via tap. Если есть фраза в текущей эпохе которая не
  // собрана — она ближайшая (вопрос времени). Если все собраны — намёк на
  // следующую эпоху.
  // JB 2026-05-26 production fix: numeric layer phrases («+1», «+2»…)
  // НЕ показываются в Atlas как «фразы-осознания» — это технические
  // ярлыки floating-text счётчика. Здесь тоже считаем только смысловые
  // слои, чтобы счётчик «ещё ждут» совпадал с тем, что видно в Atlas.
  const currentEpochPhrases = ALL_PHRASES.filter(
    (p) => p.epoch === state.epoch && p.layer !== 'numeric',
  );
  const uncollectedInEpoch = currentEpochPhrases.filter((p) => !state.atlasCollected.has(p.id));
  if (uncollectedInEpoch.length > 0) {
    return `${uncollectedInEpoch.length.toString()} ещё ждут — тапай дальше в этой эпохе`;
  }
  const nextEpoch = state.epoch + 1;
  const nextEpochPhrases = ALL_PHRASES.filter(
    (p) => p.epoch === nextEpoch && p.layer !== 'numeric',
  );
  if (nextEpochPhrases.length > 0) {
    return `новые фразы — в эпохе ${nextEpoch.toString()}`;
  }
  return null;
}

function nextLessonHint(state: GameState): string | null {
  // Find first unlocked lesson by epoch order then by id.
  const remaining = LESSONS_ALPHA.filter((l) => !state.lessonsUnlocked.has(l.id));
  if (remaining.length === 0) return null;
  // Сортируем: сначала уроки текущей эпохи, потом следующих.
  const sorted = [...remaining].sort((a, b) => {
    const aDist = Math.abs(a.epoch - state.epoch);
    const bDist = Math.abs(b.epoch - state.epoch);
    if (aDist !== bDist) return aDist - bDist;
    return a.id.localeCompare(b.id);
  });
  const next = sorted[0];
  if (next === undefined) return null;
  return `«${next.title}»: ${describeLessonCondition(next.condition, state)}`;
}

function describeLessonCondition(condition: LessonCondition, state: GameState): string {
  switch (condition.kind) {
    case 'firstTap':
      return 'сделай первый тап';
    case 'awarenessesAtLeast': {
      const remaining = Math.max(0, condition.value - state.resources.awarenesses);
      return `накопи ещё ${remaining.toString()} осознаний`;
    }
    case 'spiritBallsAtLeast': {
      const remaining = Math.max(0, condition.value - state.resources.spirit_balls);
      return `накопи ещё ${remaining.toString()} духобаллов`;
    }
    case 'tapsInRhythm': {
      const remaining = Math.max(0, condition.value - state.stats.tapsInRhythmBest);
      return remaining === 0
        ? 'уже выполнен — проверь триггер'
        : `держи серию из ${condition.value.toString()} тапов в ритм`;
    }
    case 'crystallizationsAtLeast': {
      const remaining = Math.max(0, condition.value - state.stats.totalCrystallizations);
      return `сделай ещё ${remaining.toString()} кристаллизаций`;
    }
    case 'returnAfterHours':
      return `вернись через ${condition.value.toString()} часов`;
    case 'daysStreak':
      return `${condition.value.toString()} дней подряд`;
    case 'tapAtHour':
      return `тапни в часы ${condition.fromHour.toString()}-${condition.toHour.toString()}`;
    case 'collectPhrasesInEpoch':
      return `собери ${condition.count.toString()} фраз эпохи ${condition.epoch.toString()}`;
    case 'collectAllPhrasesInEpoch':
      return `собери все фразы эпохи ${condition.epoch.toString()}`;
    case 'collectAllPhrasesInLayer':
      return `собери все фразы слоя «${condition.layer}»`;
    case 'reachEpoch':
      return `достигни эпохи ${condition.epoch.toString()}`;
    case 'silenceSecondsAtLeast':
      return `${condition.value.toString()} сек тишины`;
    case 'profileNameSet':
      return 'назови себя';
    case 'goldenSparkCaught':
      return `поймай ${condition.count.toString()} Золотых Искр`;
  }
}

function nextArtifactHint(state: GameState, unlockedArtifacts: ReadonlySet<string>): string | null {
  const remaining = VISUAL_ARTIFACTS_CATALOG.filter((a) => !unlockedArtifacts.has(a.id));
  if (remaining.length === 0) return null;
  // Sort by "distance to trigger" — наиболее близкие первыми.
  const withDistance = remaining
    .map((a) => ({ artifact: a, distance: artifactDistance(a, state) }))
    .filter((x) => x.distance !== null)
    .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  const closest = withDistance[0];
  if (closest === undefined) {
    return `${remaining.length.toString()} ещё спрятаны — играй дальше`;
  }
  return `«${closest.artifact.displayName}»: ${describeArtifactTrigger(closest.artifact, state)}`;
}

function artifactDistance(artifact: VisualArtifact, state: GameState): number | null {
  switch (artifact.trigger.kind) {
    case 'crystallizationsAtLeast':
    case 'crystallizationsExactly':
      return Math.max(0, artifact.trigger.value - state.stats.totalCrystallizations);
    case 'totalTapsAtLeast':
      return Math.max(0, artifact.trigger.value - state.stats.totalTaps);
    case 'spiritBallsAtLeast':
      return Math.max(0, artifact.trigger.value - state.resources.spirit_balls);
    case 'rhythmStreakReached':
      return Math.max(0, artifact.trigger.value - state.stats.tapsInRhythmBest);
    case 'epochReached':
      return Math.max(0, artifact.trigger.epoch - state.epoch);
    case 'profileNameSet':
      return state.profile.displayName === null ? 1 : 0;
    case 'upgradePurchasedExactly': {
      const level = state.purchasedUpgrades.get(artifact.trigger.upgradeId) ?? 0;
      return Math.max(0, artifact.trigger.level - level);
    }
    // Триггеры без числовой дистанции — отфильтруем.
    case 'lessonsUnlockedAtLeast':
    case 'phrasesCollectedAtLeast':
    case 'daysPlayedAtLeast':
    case 'activationsSeenAtLeast':
    case 'goldenSparksCaughtAtLeast':
    case 'totalUpgradesAtLeast':
    case 'firstTapInTimeWindow':
      return null;
  }
}

function describeArtifactTrigger(artifact: VisualArtifact, state: GameState): string {
  const t = artifact.trigger;
  switch (t.kind) {
    case 'crystallizationsAtLeast':
    case 'crystallizationsExactly': {
      const left = Math.max(0, t.value - state.stats.totalCrystallizations);
      return `ещё ${left.toString()} кристаллизаций`;
    }
    case 'totalTapsAtLeast': {
      const left = Math.max(0, t.value - state.stats.totalTaps);
      return `ещё ${left.toString()} тапов`;
    }
    case 'spiritBallsAtLeast': {
      const left = Math.max(0, t.value - state.resources.spirit_balls);
      return `ещё ${left.toString()} духобаллов`;
    }
    case 'rhythmStreakReached': {
      const left = Math.max(0, t.value - state.stats.tapsInRhythmBest);
      return left === 0 ? 'почти открыт' : `серия из ${t.value.toString()} тапов`;
    }
    case 'epochReached':
      return `эпоха ${t.epoch.toString()}`;
    case 'profileNameSet':
      return 'назови себя';
    case 'upgradePurchasedExactly':
      return `купи апгрейд «${t.upgradeId}» до уровня ${t.level.toString()}`;
    case 'lessonsUnlockedAtLeast':
      return `открой ${t.value.toString()} уроков`;
    case 'phrasesCollectedAtLeast':
      return `собери ${t.value.toString()} фраз`;
    case 'daysPlayedAtLeast':
      return `${t.value.toString()} дней игры`;
    case 'activationsSeenAtLeast':
      return `прочитай ${t.value.toString()} активаций`;
    case 'goldenSparksCaughtAtLeast':
      return `поймай ${t.value.toString()} Золотых Искр`;
    case 'totalUpgradesAtLeast':
      return `купи ${t.value.toString()} апгрейдов`;
    case 'firstTapInTimeWindow':
      return `тапни в часы ${t.fromHour.toString()}-${t.toHour.toString()}`;
  }
}

function nextUpgradeHint(state: GameState): string | null {
  // Find smallest-cost upgrade that is unlocked (epoch + deps) but not purchased
  // at all (level 0).
  const candidates = UPGRADES_ALPHA.filter((u) => {
    if (state.epoch < u.epoch) return false;
    if (u.requires !== undefined) {
      const reqLevel = state.purchasedUpgrades.get(u.requires.upgradeId) ?? 0;
      if (reqLevel < u.requires.minLevel) return false;
    }
    return (state.purchasedUpgrades.get(u.id) ?? 0) === 0;
  });
  if (candidates.length === 0) {
    // Все unlocked апгрейды куплены хотя бы раз. Подсказка про следующую эпоху.
    const lockedByEpoch = UPGRADES_ALPHA.filter((u) => state.epoch < u.epoch);
    if (lockedByEpoch.length > 0) {
      const closest = [...lockedByEpoch].sort((a, b) => a.epoch - b.epoch)[0]!;
      return `«${closest.name}» откроется в эпохе ${closest.epoch.toString()}`;
    }
    return null;
  }
  const sorted = [...candidates].sort((a, b) => a.baseCost - b.baseCost);
  const next = sorted[0];
  if (next === undefined) return null;
  const have = state.resources[next.currency];
  const left = Math.max(0, next.baseCost - have);
  if (left === 0) {
    return `«${next.name}»: можно купить сейчас`;
  }
  const cur = next.currency === 'spirit_balls' ? 'духобаллов' : 'осознаний';
  return `«${next.name}»: ещё ${left.toString()} ${cur}`;
}

function nextWhisperHint(_state: GameState): string | null {
  // Whispers пока не имеют unlock — все доступны как «контент-зрелище».
  // Когда добавим whisper-collection — здесь будет реальная логика.
  if (WHISPERS_ALPHA.length === 0) return null;
  return null;
}
